import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImageUpload from "@/pages/admin/ImageUpload";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Sparkles, Loader2 } from "lucide-react";

export default function CrudManager({ title, endpoint, fields, listParams = "", renderTitle }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const load = () => api.get(`/${endpoint}${listParams}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const openNew = () => {
    const init = {};
    fields.forEach((f) => { if (f.type === "switch") init[f.key] = f.default ?? false; });
    setEditing(null); setForm(init); setOpen(true);
  };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/${endpoint}/${editing.id}`, form);
      else await api.post(`/${endpoint}`, form);
      toast.success("Salvato");
      setOpen(false); load();
    } catch (e) { toast.error("Errore nel salvataggio"); } finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm("Eliminare questo elemento?")) return;
    try { await api.delete(`/${endpoint}/${it.id}`); load(); toast.success("Eliminato"); } catch (e) { toast.error("Errore"); }
  };

  const genAI = async () => {
    setAiLoading(true);
    try {
      const res = await api.post("/ai/describe", { nome: form.nome, categoria: form.categoria, hint: form.descrizione });
      set("descrizione", res.data.description);
      toast.success("Descrizione generata");
    } catch (e) { toast.error("AI non disponibile"); } finally { setAiLoading(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl text-bordeaux">{title}</h2>
        <Button onClick={openNew} className="bg-bordeaux hover:bg-bordeaux-dark text-cream" data-testid={`${endpoint}-add`}><Plus className="w-4 h-4 mr-1" />Aggiungi</Button>
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between bg-white rounded-lg border border-brass/15 px-4 py-3" data-testid={`${endpoint}-row-${it.id}`}>
            <div className="min-w-0">
              <p className="font-medium text-oak truncate">{renderTitle ? renderTitle(it) : (it.nome || it.titolo || it.title || it.year)}</p>
              <p className="text-xs text-oak/50 truncate">{it.categoria || it.date || it.year || ""} {it.available === false ? "· disattivato" : ""} {it.sold_out ? "· SOLD OUT" : ""} {it.published === false ? "· bozza" : ""}</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`${endpoint}-edit-${it.id}`}><Pencil className="w-4 h-4 text-oak/70" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove(it)} data-testid={`${endpoint}-del-${it.id}`}><Trash2 className="w-4 h-4 text-bordeaux" /></Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-oak/50 text-sm py-6 text-center">Nessun elemento. Aggiungi il primo.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-cream max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-xl text-bordeaux">{editing ? "Modifica" : "Nuovo"} — {title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {fields.map((f) => (
              <div key={f.key}>
                {f.type === "image" && <ImageUpload label={f.label} value={form[f.key]} onChange={(v) => set(f.key, v)} accept={f.accept} testid={`${endpoint}-${f.key}`} />}
                {f.type === "text" && (<><Label className="text-xs uppercase tracking-wider text-oak/70">{f.label}</Label><Input value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder} data-testid={`${endpoint}-${f.key}`} /></>)}
                {f.type === "number" && (<><Label className="text-xs uppercase tracking-wider text-oak/70">{f.label}</Label><Input value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder} data-testid={`${endpoint}-${f.key}`} /></>)}
                {f.type === "textarea" && (
                  <>
                    <div className="flex items-center justify-between"><Label className="text-xs uppercase tracking-wider text-oak/70">{f.label}</Label>
                      {f.ai && <Button type="button" size="sm" variant="outline" onClick={genAI} disabled={aiLoading || !form.nome} data-testid={`${endpoint}-ai`}>{aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}AI</Button>}
                    </div>
                    <Textarea value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`${endpoint}-${f.key}`} />
                  </>
                )}
                {f.type === "select" && (
                  <><Label className="text-xs uppercase tracking-wider text-oak/70">{f.label}</Label>
                    <Select value={form[f.key] || ""} onValueChange={(v) => set(f.key, v)}>
                      <SelectTrigger data-testid={`${endpoint}-${f.key}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>{f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </>
                )}
                {f.type === "switch" && (
                  <label className="flex items-center justify-between py-1 cursor-pointer">
                    <span className="text-sm text-oak/80">{f.label}</span>
                    <Switch checked={f.invert ? form[f.key] === false : !!form[f.key]} onCheckedChange={(v) => set(f.key, f.invert ? !v : v)} data-testid={`${endpoint}-${f.key}`} />
                  </label>
                )}
              </div>
            ))}
            <Button onClick={save} disabled={saving} className="w-full bg-bordeaux hover:bg-bordeaux-dark text-cream" data-testid={`${endpoint}-save`}>{saving ? "Salvataggio…" : "Salva"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
